import React, { useEffect, useState } from 'react';
import { X, Loader2, BookOpen } from 'lucide-react';
import MarkdownRenderer from '@stepflow/ui-shared/components/MarkdownRenderer.jsx';
import { getContextVersion, approveContextVersion } from '@stepflow/ui-shared/services/dataPlaneApi';

/**
 * THE CONTEXT CARD (CONTEXT-DOCS.md §P3) — a build's result in the stream, shown like a shared document but titled
 * "Context — generated … (vN)": the book icon, the title, "N diagrams · from N messages + N docs". Click → the
 * Context pane.
 */
export function ContextCard({ m, onOpen, active = false }) {
  const c = m.context || {};
  const bits = [c.diagrams ? `${c.diagrams} diagram${c.diagrams === 1 ? '' : 's'}` : '',
    (c.messages || c.docs) ? `from ${c.messages || 0} message${c.messages === 1 ? '' : 's'}${c.docs ? ` + ${c.docs} doc${c.docs === 1 ? '' : 's'}` : ''}` : '']
    .filter(Boolean).join(' · ');
  return (
    <div className="pl-11" data-testid={`context-msg-${m.id}`} data-kind="context">
      <button type="button" data-testid={`context-card-${m.id}`} onClick={() => onOpen(m)}
        className={`flex w-80 max-w-full items-center gap-2.5 rounded-lg border px-2.5 py-2 text-left transition-colors ${
          active ? 'border-indigo-300 ring-1 ring-indigo-200' : 'border-indigo-100 bg-indigo-50/40 hover:border-indigo-200 hover:bg-indigo-50'}`}>
        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-indigo-100 text-indigo-700" aria-hidden>
          <BookOpen className="h-[1.125rem] w-[1.125rem]" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[0.8125rem] font-semibold text-gray-900">{c.title || m.text}</span>
          <span className="block truncate text-[0.6875rem] text-gray-500">{bits || 'The project context'}</span>
        </span>
      </button>
    </div>
  );
}

/**
 * THE CONTEXT PANE — one generated version, rendered (Markdown + its Mermaid diagrams), in the SAME column as the
 * Thread / Doc pane (one at a time). Header: the title, Active (what agents get) or Earlier version, ✕ (Esc closes).
 * The lane (RULES / REFERENCE / OFF) and re-activation live in the Context tab.
 */
export default function ContextPane({ networkId, poolId, message, onClose, canApprove = false, onApproved = null }) {
  const v = (message.context || {}).version;
  const [state, setState] = useState({ loading: true, data: null, error: '' });
  useEffect(() => {
    let gone = false;
    setState({ loading: true, data: null, error: '' });
    Promise.resolve()
      .then(() => getContextVersion(networkId, v, { pool: poolId }))
      .then((d) => { if (!gone) setState({ loading: false, data: d, error: '' }); })
      .catch((e) => { if (!gone) setState({ loading: false, data: null, error: (e && e.message) || "Couldn't open this context." }); });
    return () => { gone = true; };
  }, [networkId, poolId, v]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const d = state.data || {};
  const [approving, setApproving] = useState(false);
  const approve = async () => {
    setApproving(true);
    try {
      const r = await approveContextVersion(networkId, v, { pool: poolId });
      setState((s) => ({ ...s, data: { ...(s.data || {}), approvedBy: r && r.approvedBy, approvedAt: r && r.approvedAt } }));
      if (onApproved) onApproved(v);
    } catch (e) {
      setState((s) => ({ ...s, error: (e && e.message) || "Couldn't approve it." }));
    }
    setApproving(false);
  };
  return (
    <aside data-testid="context-pane" role="dialog" aria-label="Context"
      className="hidden w-[28rem] shrink-0 flex-col border-l border-gray-200 bg-white md:flex">
      <div className="flex h-12 shrink-0 items-center gap-2 border-b border-gray-200 px-4">
        <div className="min-w-0 flex-1">
          <div className="truncate text-[0.9375rem] font-bold text-gray-900">{d.title || (message.context || {}).title || 'Context'}</div>
          {state.data ? (
            <div className="text-[0.75rem]" data-testid="context-pane-state">
              {d.active
                ? <span className="font-medium text-emerald-700">Active — this is what agents and Plan use</span>
                : <span className="text-gray-500">An earlier version — turn it back on in the Context tab</span>}
              {d.approvedBy
                ? <span data-testid="context-pane-approved" className="ml-1.5 text-gray-500">· Approved by {d.approvedBy}</span>
                : null}
            </div>
          ) : null}
        </div>
        {state.data && d.active && !d.approvedBy && canApprove ? (
          <button type="button" data-testid="context-pane-approve" onClick={approve} disabled={approving}
            title="Sign this version off — Plan works only from an approved context"
            className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60">
            {approving ? 'Approving…' : 'Approve'}
          </button>
        ) : null}
        <button type="button" data-testid="context-pane-close" aria-label="Close" title="Close" onClick={onClose}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900">
          <X className="h-[1.125rem] w-[1.125rem]" aria-hidden />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto px-4 py-3" data-testid="context-pane-body">
        {state.loading ? (
          <div className="flex justify-center py-8 text-[0.8125rem] text-gray-400"><Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden /> Opening…</div>
        ) : state.error ? (
          <div data-testid="context-pane-error" className="py-8 text-center text-[0.8125rem] text-rose-600">{state.error}</div>
        ) : (
          <MarkdownRenderer content={d.text || ''} size="chat" />
        )}
      </div>
    </aside>
  );
}
